import { useMemo } from "react";
import { SyncOptions, syncDateRange } from "./dateRange";

export type DateInputProps = {
  type: "date";
  value: string;
  onChange: (e: { target: { value: string } }) => void;
  min?: string;
  max?: string;
};

export type UseDateRangeResult = {
  fromProps: DateInputProps;
  toProps: DateInputProps;
  /** Direkte Setter — nützlich z. B. für Reset-Buttons. */
  setFrom: (v: string) => void;
  setTo: (v: string) => void;
};

/**
 * Kapselt Synchronisierungs-Logik für ein Paar aus Anreise-/Abreise-Datumsfeldern.
 * Aufrufer übergibt Werte + Setter (oder einen kombinierten Setter) und erhält
 * `fromProps`/`toProps` zum Spreaden auf <input type="date"> oder Atome wie <Input/>.
 */
export function useDateRange(
  from: string,
  to: string,
  onChange: (next: { from: string; to: string }) => void,
  opts: SyncOptions = {},
): UseDateRangeResult {
  return useMemo(() => {
    const setFrom = (next: string) => {
      const synced = syncDateRange({ from, to }, "from", next, opts);
      onChange(synced);
    };
    const setTo = (next: string) => {
      const synced = syncDateRange({ from, to }, "to", next, opts);
      onChange(synced);
    };
    const minNights = opts.minNights ?? 1;
    // min/max für native Picker: Abreise darf nicht vor Anreise+minNights liegen.
    const toMin = from ? addDaysISO(from, minNights) : undefined;
    const fromMax = to && minNights > 0 ? addDaysISO(to, -minNights) : undefined;
    return {
      fromProps: {
        type: "date",
        value: from,
        max: fromMax,
        onChange: (e) => setFrom(e.target.value),
      },
      toProps: {
        type: "date",
        value: to,
        min: toMin,
        onChange: (e) => setTo(e.target.value),
      },
      setFrom,
      setTo,
    };
  }, [from, to, onChange, opts.minNights, opts.keepDuration]);
}

function addDaysISO(value: string, days: number): string {
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return value;
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}
